import { sendMarketingRequest, trackEvent } from '@patrianna/analytics'
import type { GameProduct } from '@patrianna/shared-patrianna-types/store/GameModule'
import * as Sentry from '@sentry/nextjs'

import { getBrowserDeviceInfo } from 'analyticActions/gameEvents/utils'
import { api } from 'services/gateway'
import type { TypedThunk } from 'src/store'
import { getDataFromSessionStorage, INTERNAL_GAME_NAVIGATION } from 'src/utils/sessionStorage'
import { getLocationDataSelector } from 'store/modules/appConfig/selectors'
import { getGoldCurrencySelector, getSweepstakeCurrencySelector } from 'store/modules/currencies/selectors'
import { getUserSelector } from 'store/modules/user/selectors'

type GameEventResponse = { applied: boolean }

type GameEventRequest = {
  browser: string
  code: string
  country: string
  currency: string
  device: string
  eventType: 'OPEN_GAME' | 'CLOSE_GAME'
  gameType: string
  gcBalance: string
  location: string
  name: string
  os: string
  scBalance: string
  state: string
  type: 'GameEventRequest'
  category?: string
  gameIndex?: number
  swimlaneIndex?: number
  referrerPath?: string
  xpageIndex?: number
  ypageIndex?: number
  layout?: string
}

const formatGameDetails = (gameProduct: GameProduct) => {
  if (gameProduct.type === 'bingo') {
    return {
      gameCode: gameProduct.code === 'bingo' ? 'bingo' : gameProduct.code,
      gameName: gameProduct.code === 'bingo' ? 'Bingo lobby' : `Room ${gameProduct.code}`,
    }
  }

  return { gameCode: gameProduct.code, gameName: gameProduct.title }
}

export const trackGameEvent =
  (
    eventName: 'OPEN_GAME' | 'CLOSE_GAME',
    gameProduct: GameProduct,
    currency: string,
    category?: string,
    gameIndex?: string,
    swimlaneIndex?: string,
    refererPath?: string,
    xPageIndex?: string,
    yPageIndex?: string,
    layout?: string,
  ): TypedThunk =>
  (dispatch, getState, { gateway, errorHandler }) => {
    const state = getState()
    const user = getUserSelector(state)
    const sweepstakeCurrency = getSweepstakeCurrencySelector(state)?.amount || 0
    const goldCurrency = getGoldCurrencySelector(state)?.amount || 0
    const location = getLocationDataSelector(state)
    const internalGameNavigationFlag = getDataFromSessionStorage(INTERNAL_GAME_NAVIGATION) === 'true'
    const DIRECT_URL = 'directUrl'
    const resolvedRefererPath = refererPath ?? DIRECT_URL
    const gameOpeningParams = internalGameNavigationFlag
      ? {
          category,
          gameIndex: gameIndex ? Number(gameIndex) : undefined,
          swimlaneIndex: swimlaneIndex ? Number(swimlaneIndex) : undefined,
          referrerPath: resolvedRefererPath,
          xpageIndex: xPageIndex ? Number(xPageIndex) : undefined,
          ypageIndex: yPageIndex ? Number(yPageIndex) : undefined,
          layout: layout || undefined,
        }
      : { referrerPath: DIRECT_URL }

    const { gameCode, gameName } = formatGameDetails(gameProduct)
    const { browser, device, OS } = getBrowserDeviceInfo()

    if (user) {
      sendMarketingRequest(api.setAccountMarketingInfo)
    }

    if (!resolvedRefererPath) {
      Sentry.captureMessage('trackGameEvent -  empty game opening params')
    }

    if (!layout) {
      Sentry.captureMessage(`trackGameEvent -  empty layout with refererPath: ${resolvedRefererPath}`)
    }

    const data: GameEventRequest = {
      browser,
      ...gameOpeningParams,
      code: gameCode,
      country: location.country,
      currency,
      device,
      eventType: eventName,
      gameType: gameProduct.type,
      gcBalance: String(goldCurrency),
      location: window.location.href,
      name: gameName,
      os: OS,
      scBalance: String(sweepstakeCurrency),
      state: location.state,
      type: 'GameEventRequest',
    }

    gateway
      .emit<GameEventResponse>(data)
      .then((res) => res?.applied && trackEvent(eventName))
      .catch((err) => dispatch(errorHandler(err, data)))
  }
